import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import OutOfOfficeMark from './OutOfOfficeMark.js';
import './dropdowns.css';

function defaultGetOptionDisplayText(option) {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  const display = option.display || option.name || option.description || '';
  if (display) return display;
  if (typeof option.code === 'string' && Array.isArray(option.terms)) {
    const primary = option.terms[0];
    const c = option.code;
    return primary ? `(${c}) ${primary}` : `(${c})`;
  }
  return '';
}

function defaultSearchableMatch(option, queryLower) {
  if (!queryLower) return true;
  const display = defaultGetOptionDisplayText(option).toLowerCase();
  if (display.includes(queryLower)) return true;

  const code =
    typeof option.code === 'string'
      ? option.code
      : option.code?.code != null
        ? String(option.code.code)
        : '';
  if (code && code.toLowerCase().includes(queryLower)) return true;

  const terms = option.terms;
  if (Array.isArray(terms)) {
    return terms.some((t) => t && String(t).toLowerCase().includes(queryLower));
  }

  const value = option.value != null ? String(option.value).toLowerCase() : '';
  if (value && value.includes(queryLower)) return true;

  return false;
}

// Unified CustomDropdown component that combines features from various implementations.
// Pass searchable={true} for a combobox-style control: editable filter + dropdown list (default is plain dropdown).
export const CustomDropdown = ({
  selected,
  selectedOption,
  selectedIcon,
  options,
  isOpen,
  toggleDropdown,
  onSelect,
  className = '',
  containerRef,
  dropdownRef,
  keyExtractor,
  disabled = false,
  width,
  maxHeight = '300px',
  /** When true, header is an input that filters options (client-side). Default false keeps legacy behaviour. */
  searchable = false,
  searchPlaceholder,
  /** `(option, queryLowercase) => boolean` — customise matching when searchable */
  filterOption,
  placeholder = '',
  /** When true, renders a compact pill-style trigger. Does not affect other usages of CustomDropdown. */
  compact = false,
  /** When true and searchable, prepends a free-text option (pencil icon) as the first item whenever the user is typing. Selecting it calls onSelect with { code: 'CUSTOM', terms: [typedText] }. */
  allowFreeText = false,
  /** Stored out-of-office status for the closed selection. Shown beside the label when currently out. */
  selectedOutOfOffice = null,
}) => {
  const displayValue = selectedOption || selected;
  const [searchQuery, setSearchQuery] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const internalRef = useRef(null);
  const refToUse = dropdownRef || containerRef || internalRef;
  const inputRef = useRef(null);
  const optionsListRef = useRef(null);

  const matchFn = filterOption || defaultSearchableMatch;

  const visibleOptions = useMemo(() => {
    let base;
    if (!searchable || !isOpen) {
      base = options || [];
    } else {
      // searchQuery null = just opened, show all; '' = user cleared filter, show all
      const q = (searchQuery !== null ? searchQuery : '').trim().toLowerCase();
      base = (options || []).filter((opt) => matchFn(opt, q));
    }

    if (allowFreeText && searchable && isOpen && searchQuery !== null && searchQuery.trim()) {
      return [{ code: 'CUSTOM', terms: [searchQuery.trim()], _isFreeText: true }, ...base];
    }

    return base;
  }, [options, searchable, isOpen, searchQuery, matchFn, allowFreeText]);

  useEffect(() => {
    if (!searchable) return;
    if (isOpen) {
      // null = show full list; input still displays selection until user types
      setSearchQuery(null);
      setHighlightedIndex(-1);
    } else {
      setSearchQuery(null);
      setHighlightedIndex(-1);
    }
  }, [searchable, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen || !searchable) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          // Close dropdown - useEffect will handle resetting search query
          if (toggleDropdown) {
            toggleDropdown();
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev < visibleOptions.length - 1 ? prev + 1 : 0;
            return next;
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : visibleOptions.length - 1;
            return next;
          });
          break;

        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < visibleOptions.length) {
            onSelect(visibleOptions[highlightedIndex]);
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, searchable, highlightedIndex, visibleOptions, toggleDropdown, onSelect]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0 || !optionsListRef.current) return;

    const optionElements = optionsListRef.current.querySelectorAll('[role="option"]');
    const highlightedElement = optionElements[highlightedIndex];
    
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (refToUse.current && !refToUse.current.contains(event.target)) {
        // Close dropdown - useEffect will handle resetting search query
        if (toggleDropdown) {
          toggleDropdown();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleDropdown, refToUse]);

  // Auto-focus input when dropdown opens in searchable mode
  useEffect(() => {
    if (searchable && isOpen && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
        // Select all text so user can immediately start typing to filter
        inputRef.current?.select();
      }, 0);
    }
  }, [searchable, isOpen]);

  const handleToggle = useCallback(() => {
    if (disabled || !toggleDropdown) return;
    toggleDropdown();
  }, [disabled, toggleDropdown]);

  const headerInputValue =
    searchable && isOpen
      ? searchQuery !== null
        ? searchQuery
        : displayValue
          ? String(displayValue)
          : ''
      : displayValue
        ? String(displayValue)
        : '';

  const showHeaderPlaceholder =
    searchable && !isOpen && !(displayValue != null && String(displayValue).trim());

  const selectedValue = (
    <div className={`select-selected-value${selectedOutOfOffice ? ' select-selected-value--away' : ''}`}>
      {selectedIcon && (
        <FontAwesomeIcon icon={selectedIcon} className="dropdown-option-icon" />
      )}
      {searchable ? (
        <input
          ref={inputRef}
          type="text"
          className="select-search-input"
          disabled={disabled}
          placeholder={showHeaderPlaceholder ? searchPlaceholder || placeholder : undefined}
          value={headerInputValue}
          onChange={(e) => {
            if (!searchable || disabled) return;
            setSearchQuery(e.target.value);
            setHighlightedIndex(-1);
            if (!isOpen && toggleDropdown) {
              toggleDropdown();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            if (!isOpen && toggleDropdown) {
              toggleDropdown();
            }
          }}
          onFocus={(e) => {
            e.stopPropagation();
            if (disabled) return;
            if (!isOpen && toggleDropdown) {
              toggleDropdown();
            }
          }}
          readOnly={!isOpen}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
      ) : (
        <span>{displayValue}</span>
      )}
      <OutOfOfficeMark outOfOffice={selectedOutOfOffice} />
    </div>
  );

  return (
    <div
      className={`custom-select ${isOpen ? 'open' : ''} ${searchable ? 'custom-select--searchable' : ''} ${compact ? 'custom-select--compact' : ''} ${className}`}
      ref={refToUse}
      style={width ? { minWidth: width } : undefined}
    >
      {searchable ? (
        <div
          className="select-selected select-selected--searchable"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onClick={disabled ? undefined : handleToggle}
        >
          {selectedValue}
        </div>
      ) : (
        <div
          className="select-selected"
          onClick={disabled ? undefined : handleToggle}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {selectedValue}
        </div>
      )}
      <div
        ref={optionsListRef}
        className={`select-items ${isOpen ? '' : 'select-hide'}`}
        style={{
          pointerEvents: disabled ? 'none' : 'auto',
          maxHeight,
          overflowY: 'auto',
        }}
      >
        {visibleOptions.length === 0 ? (
          <div className="select-items-empty">No matches</div>
        ) : (
          visibleOptions.map((option, index) => {
            const isFreeText = option._isFreeText === true;
            const displayText = isFreeText
              ? option.terms[0]
              : defaultGetOptionDisplayText(option) ||
                (typeof option === 'string' || typeof option === 'number' ? String(option) : '');
            const hasIcon = !isFreeText && option.icon;
            const isHighlighted = index === highlightedIndex;

            return (
              <div
                key={
                  isFreeText
                    ? `freetext-${option.terms[0]}`
                    : keyExtractor
                      ? keyExtractor(option, index)
                      : option.id || option.value || option.userId || option.code || option || index
                }
                role="option"
                onMouseDown={(e) => {
                  if (searchable) e.preventDefault();
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => onSelect(option)}
                className={`${isFreeText ? 'dropdown-option-freetext' : ''} ${option.outOfOffice ? 'dropdown-option-away' : ''} ${option.isBold ? 'dropdown-option-flagged' : ''} ${isHighlighted ? 'dropdown-option-highlighted' : ''}`}
              >
                {isFreeText ? (
                  <FontAwesomeIcon icon={faPencil} className="dropdown-option-icon dropdown-option-icon--freetext" />
                ) : hasIcon ? (
                  <FontAwesomeIcon icon={option.icon} className={`dropdown-option-icon${option.iconClassName ? ` ${option.iconClassName}` : ''}`} />
                ) : null}
                <span>{displayText}</span>
                {!isFreeText && option.outOfOffice ? (
                  <OutOfOfficeMark outOfOffice={option.outOfOffice} />
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
